/** Session keys for inline OAuth return (chat → Google → callback → chat). */
export const GOOGLE_DRIVE_SESSION = {
  CUSTOMER_ID: "google_drive_oauth_customer_id",
  RETURN_TO: "google_drive_oauth_return_to",
  NONCE: "google_drive_oauth_nonce",
  /** Set (to the customer id) right after a successful token exchange so the chat
   * window can open the picker automatically on return, instead of making the user
   * click "Google Drive" a second time. Cleared by whoever consumes it. */
  AUTO_OPEN: "google_drive_oauth_auto_open",
  /** Set only by a chat-initiated OAuth flow. The callback consults it to decide
   * whether to arm AUTO_OPEN: connecting from the Connect tab must not make the
   * Picker ambush the user the next time they open chat. Cleared by the callback. */
  AUTO_OPEN_INTENT: "google_drive_oauth_auto_open_intent",
} as const;

/**
 * Prefer the Drive-specific OAuth client id when set; otherwise fall back to the shared
 * REACT_APP_GOOGLE_CLIENT_ID (same one GMB/YouTube use). Picker API key is required.
 * Set REACT_APP_GOOGLE_DRIVE_ENABLED=false to force off.
 */
export function getGoogleDriveOAuthClientId(): string | undefined {
  const driveClientId = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID?.trim();
  if (driveClientId) return driveClientId;
  return process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || undefined;
}

export function isGoogleDriveAttachmentsConfigured(): boolean {
  if (process.env.REACT_APP_GOOGLE_DRIVE_ENABLED === "false") {
    return false;
  }
  const clientId = getGoogleDriveOAuthClientId();
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY?.trim();
  return Boolean(clientId && apiKey);
}

export function getGoogleDriveOAuthRedirectUri(): string {
  return `${window.location.origin}/connect/google-drive/callback`;
}

/**
 * Remember the connected Drive profile id (Google `sub`) per customer. The backend
 * access-token endpoint needs it to look up the stored refresh token by exact secret
 * name (a GetSecretValue the service role is allowed to do) instead of listing all
 * secrets (which the role isn't allowed to do). Persisted from the OAuth callback.
 */
const GOOGLE_DRIVE_PROFILE_ID_PREFIX = "google_drive_profile_id_";

export function rememberGoogleDriveProfileId(
  customerId: string | undefined,
  profileId: string | undefined
): void {
  if (!customerId || !profileId) return;
  try {
    localStorage.setItem(`${GOOGLE_DRIVE_PROFILE_ID_PREFIX}${customerId}`, profileId);
  } catch {
    // Storage may be unavailable (private mode / quota) — non-fatal.
  }
}

export function getRememberedGoogleDriveProfileId(
  customerId: string | undefined
): string | undefined {
  if (!customerId) return undefined;
  try {
    return (
      localStorage.getItem(`${GOOGLE_DRIVE_PROFILE_ID_PREFIX}${customerId}`) || undefined
    );
  } catch {
    return undefined;
  }
}
